import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  ChangeEvent,
} from "react";
import { useIntl } from "react-intl";
import {
  ApplicantFormData,
  JobFormAnswerModel,
  JobFormModel,
  AnswerType,
  WorkAvailability,
} from "../../shared/models";
import DynamicField from "./dynamic-field";
import AddressAutocomplete from "./address-autocomplete";
import {
  acceptedResumeTypes,
  ConvyDiscipline,
  ConvyHardSkill,
  ConvySchoolType,
  ConvyStudyPath,
} from "./career-page.models";
import {
  EditCareerSiteModel,
  FieldPreference,
} from "../../pages/career-page/job-detail/job-detail.model";
import { formatSalary } from "../../shared/currency-utils";
import { nationalities } from "../../shared/consts";

type ResumeFormProps = {
  onSubmit: (data: ApplicantFormData) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  privacyPolicyLinks: string[];
  color: string;
  radius: string;
  formTitle: string;
  buttonText: string;
  showOnlyResumeDrop?: boolean;
  questions?: JobFormModel[];
  careerSettings: EditCareerSiteModel[];
  boardCode?: string;
  customerId: string;
};

const ResumeForm: React.FC<ResumeFormProps> = ({
  onSubmit,
  loading,
  setLoading,
  privacyPolicyLinks,
  color,
  radius,
  formTitle,
  buttonText,
  showOnlyResumeDrop = false,
  questions = [],
  careerSettings,
  boardCode,
  customerId,
}) => {
  const IsConvy = boardCode === "convy";
  const intl = useIntl();

  const [name, setName] = useState<string | null>(null);
  const [familyName, setFamilyName] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [resume, setResume] = useState<string | ArrayBuffer | null>(null);
  const [thankYouMessage] = useState<string | null>(null);
  const [privacyConsent, setPrivacyConsent] = useState<boolean>(false);
  const [newsletterConsent, setnewsletterConsent] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState<string | null>(null);
  const [province, setProvince] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [forms] = useState<JobFormModel[]>(questions);
  const [answerForms, setAnswerForms] = useState<JobFormAnswerModel[] | null>(
    null
  );
  const [applicantPreferences, setApplicantPreferences] = useState<
    string | null
  >(null);
  const [fiscalCode, setFiscalCode] = useState<string | null>(null);
  const [citizenWorkStatus, setCitizenWorkStatus] = useState<string | null>(
    null
  );
  const [jobQualification, setJobQualification] = useState<string | null>(null);
  const [commercialLicense, setCommercialLicense] = useState<boolean | null>(
    null
  );
  const [desiredSalary, setDesiredSalary] = useState<number | null>(null);
  const [desiredSalaryInput, setDesiredSalaryInput] = useState<string>("");
  const [disabilityPercentage, setDisabilityPercentage] = useState<
    number | null
  >(null);
  const [drivingLicense, setDrivingLicense] = useState<boolean | null>(null);
  const [felonyConviction, setFelonyConviction] = useState<boolean | null>(
    null
  );
  const [gender, setGender] = useState<string | null>(null);
  const [pilotFlightHours, setPilotFlightHours] = useState<number | null>(null);
  const [protectedCategoryStatus, setProtectedCategoryStatus] = useState<
    boolean | null
  >(null);
  const [softSkills, setSoftSkills] = useState<string[] | null>(null);
  const [hardSkills, setHardSkills] = useState<string[] | null>(null);
  const [softSkillInput, setSoftSkillInput] = useState<string | null>(null);
  const [hardSkillInput, setHardSkillInput] = useState<string | null>(null);
  const [willingnessToTravel, setWillingnessToTravel] = useState<
    boolean | null
  >(null);
  const [workAvailability, setWorkAvailability] =
    useState<WorkAvailability | null>(null);
  const [certifications, setCertifications] = useState<string[] | null>(null);
  const [certificationsInput, setCertificationsInput] = useState<string | null>(
    null
  );
  const [hobbyAndInterests, setHobbyAndInterests] = useState<string[] | null>(
    null
  );
  const [hobbyAndInterestsInput, setHobbyAndInterestsInput] = useState<
    string | null
  >(null);
  const [spokenLanguages, setSpokenLanguages] = useState<string[] | null>(null);
  const [spokenLanguagesInput, setSpokenLanguagesInput] = useState<
    string | null
  >(null);
  const [links, setLinks] = useState<string[] | null>(null);
  const [linksInput, setLinksInput] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [nationality, setNationality] = useState<string | null>(null);
  const [isAddressAutoCompleteCompiled, setIsAddressAutoCompleteCompiled] =
    useState<boolean>(false);

  const [convySchoolTypes, getConvySchoolTypes] = useState<ConvySchoolType[]>(
    []
  );
  const [selectedSchoolType, setConvySchoolType] = useState<string>();

  const [convyStudyPaths, getConvyStudyPaths] = useState<ConvyStudyPath[]>([]);
  const [selectedStudyPath, setConvyStudyPath] = useState<string>();

  const [convyDisciplines, getConvyDisciplines] = useState<ConvyDiscipline[]>(
    []
  );
  const [selectedDiscipline, setConvyDiscipline] = useState<string>();

  const [convyHardSkill, getConvyHardSkills] = useState<ConvyHardSkill[]>([]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = process.env.REACT_APP_GET_JOBPOSTS_SERVER_API_KEY;


  useEffect(() => {
    if (boardCode === 'convy') {
      fetchConvySchoolTypes();
    }
  }, [boardCode]); // Only run when boardCode changes

  useEffect(() => {
    setWorkAvailability((prev) => ({
      ...((prev as WorkAvailability) || {
        availabilityStartDate: "",
        nightAvailability: false,
        weekendAvailability: false,
        willRelocate: false,
        workOvertime: false,
      }),
      availabilityStartDate: "",
    }));
    setAnswerForms(
      forms.map((form) => ({
        formID: form.formID,
        authorID: form.authorID,
        title: form.title,
        description: form.description,
        totalScore: 0,
        submissionDate: new Date().toISOString().slice(0, 16),
        answers: form.questions.map((question) => ({
          questionID: question.questionID,
          type: question.type,
          questionText: question.questionText,
          options:
            question.options?.map((option) => ({
              option: option.text,
              isSelected: false,
              points: 0,
            })) || [],
          isMandatory: question.isMandatory,
          points: 0,
          value:
            question.type === "multipleChoice" ||
            question.type === "singleChoice"
              ? question.options
              : null,
        })),
      }))
    );
  }, [forms]);

  useEffect(() => {
    if (resumeFile) {
      const handleFile = (file: File): boolean => {
        let error = false;

        if (acceptedResumeTypes.includes(file.type)) {
          if (file.size > 6 * 1024 * 1024) {
            // max MB
            alert(
              intl.formatMessage({
                id: "careerPageApply.fileSize",
                defaultMessage: "File size must not exceed 6MB.",
              })
            );
            error = true;
          }
        } else if (!acceptedResumeTypes.includes(file.type)) {
          alert(
            intl.formatMessage({
              id: "careerPageApply.uploadFile",
              defaultMessage:
                "Please upload a valid file (PDF, ODT, DOC, DOCX, PNG, JPG).",
            })
          );
          error = true;
        }

        return error;
      };

      console.log(resumeFile);

      const error = handleFile(resumeFile);
      if (error) {
        setResume(null);
        setResumeFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setResumeFile(resumeFile);
      }
    }
  }, [resumeFile, intl, setResume, setResumeFile]);

  const handleAddressSelection = (address: {
    fullAddress: string;
    country: string;
    postalCode: string;
    city: string;
    province: string;
    address: string;
  }) => {
    // setFullAddress(address.fullAddress);
    setIsAddressAutoCompleteCompiled(true);
    setCountry(address.country);
    setPostalCode(address.postalCode);
    setCity(address.city);
    setProvince(address.province);
    setAddress(address.address);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setResumeFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile(file);
    }
  };

  const handleDynamicFieldChange = (questionID: string, value: AnswerType) => {
    setAnswerForms((prevAnswerForms) =>
      (prevAnswerForms ?? []).map((form) => ({
        ...form,
        answers: form.answers.map((answer) => {
          if (answer.questionID === questionID) {
            const isEmptyArray = Array.isArray(value) && value.length === 0;

            return {
              ...answer,
              value:
                isEmptyArray &&
                answer.type !== "multipleChoice" &&
                answer.type !== "singleChoice"
                  ? null
                  : value,
              points: answer.type === "open" ? 0 : answer.points,
            };
          }
          return answer;
        }),
      }))
    );
  };

  const fetchConvySchoolTypes = async () => {
    if (boardCode !== 'convy') return;
    try {
      const response = await fetch(`${apiUrl}/getConvyData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({url: "scuole-tipologie"})
      });

      console.log(response)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()).data;
      console.log(data)
      getConvySchoolTypes(data as ConvySchoolType[] || []);
    } catch (error) {
      console.error('Error fetching convy school types:', error);
    }
  };

  const handleSelectionSchoolType = async (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const schoolType = convySchoolTypes.find(
      (a) => String(a.id) === String(event.target.value)
    );
    setConvySchoolType(String(schoolType?.id));

    try {
      const response = await fetch(`${apiUrl}/getConvyData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({url: `percorsi-studio/${schoolType?.categoria}`})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()).data;
      getConvyStudyPaths(data as ConvyStudyPath[] || []);
    } catch (error) {
      console.error('Error fetching convy study paths:', error);
    }

    setConvyStudyPath(undefined);
    getConvyDisciplines([]);
    setConvyDiscipline(undefined);
  };

  const handleSelectionStudyPath = async (
    event: ChangeEvent<HTMLSelectElement>
  ) => {
    const studyPath = convyStudyPaths.find(
      (a) => String(a.id) === String(event.target.value)
    );
    setConvyStudyPath(String(studyPath?.id));

    try {
      const response = await fetch(`${apiUrl}/getConvyData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({url: `discipline/percorsi-studio/${studyPath?.id}`})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()).data;
      getConvyDisciplines(data as ConvyDiscipline[] || []);
    } catch (error) {
      console.error('Error fetching convy disciplines:', error);
    }
    setConvyDiscipline(undefined);
  };

  const handleSelectionDiscipline = async (event: ChangeEvent<HTMLSelectElement>) => {
    const discipline = convyDisciplines.find((a) => String(a.id) === String(event.target.value))
    setConvyDiscipline(String(discipline?.id));

    try {
      const response = await fetch(`${apiUrl}/getConvyData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({url: `competenze/disciplina/${discipline?.id}`})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()).data;
      getConvyHardSkills(data as ConvyHardSkill[] || []);
    } catch (error) {
      console.error('Error fetching convy hard skills:', error);
    }
  };

  const handleUpload = async (): Promise<
    | { success: false }
    | { success: true; sha256: string; storagePosition: string }
  > => {
    setLoading(true);
    if (!resumeFile) {
      alert(
        intl.formatMessage({
          id: "careerPageApply.validation.resume",
          defaultMessage: "Resume is required",
        })
      );
      return { success: false };
    }

    const formData = new FormData();
    formData.append("file", resumeFile);

    try {
      const response = await fetch(`${apiUrl}/generateArchiveIDAndFile`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        console.log("File uploaded successfully!");
        return {
          success: true,
          sha256: data.archiveID,
          storagePosition: data.path,
        };
      } else {
        console.error(`File upload failed.`);
        alert("File not supported, please try upload a different format.");
      }
    } catch (err) {
      alert(
        intl.formatMessage({
          id: "careerPageApply.errorMessage",
          defaultMessage: "Failed to update resume. Please try again later.",
        })
      );
      console.error(`ERROR uploading resume file: ${JSON.stringify(err)}`);
      return { success: false };
    }
    return { success: false };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const uploadResult = await handleUpload();
    if (!uploadResult.success) return;
    const { sha256, storagePosition, ..._ } = uploadResult;

    setLoading(true);

    let definedHardSkills: string[] = [];
    if (hardSkills && Array.isArray(hardSkills))
      definedHardSkills = [...definedHardSkills, ...hardSkills];
    if (convyHardSkill && Array.isArray(convyHardSkill))
      definedHardSkills = [
        ...definedHardSkills,
        ...convyHardSkill.map((hs) => hs.titolo.it),
      ];

    const formData: ApplicantFormData = {
      name,
      archiveID: sha256,
      storagePosition: storagePosition,
      familyName,
      email,
      phone,
      resume,
      privacyConsent,
      fiscalCode,
      formAnswers: (answerForms ?? []).map((form) => ({
        formID: form.formID,
        authorID: form.authorID,
        title: form.title,
        description: form.description,
        totalScore: form.totalScore,
        submissionDate: form.submissionDate,
        answers: form.answers.map((answer) => ({
          questionID: answer.questionID,
          type: answer.type,
          questionText: answer.questionText,
          points: answer.points,
          isMandatory: answer.isMandatory,
          value: answer.value,
        })),
      })),
      address: {
        street: address,
        city: city,
        postalCode: postalCode,
        province: province,
        country: country,
      },
      birthDate,
      applicantPreferences,
      citizenWorkStatus,
      jobQualification,
      commercialLicense,
      desiredSalary,
      disabilityPercentage,
      drivingLicense,
      felonyConviction,
      gender,
      pilotFlightHours,
      protectedCategoryStatus,
      softSkills,
      willingnessToTravel,
      workAvailability,
      certifications,
      hobbyAndInterests,
      languages: spokenLanguages,
      links,
      nationality,
      convyData: {
        newsletterConsent: newsletterConsent,
        schoolTypeID: selectedSchoolType,
        studyPathID: selectedStudyPath,
        disciplineID: selectedDiscipline,
      },
      hardSkills: definedHardSkills,
    };

    onSubmit(formData);
  };

  const handleCommaSeparatedInput = (
    raw: string | null,
    currentList: string[] | null,
    setList: React.Dispatch<React.SetStateAction<string[] | null>>,
    setRaw: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    if (!raw) return;

    const existingLC = new Set(
      (currentList ?? []).map((item) => item.toLowerCase())
    );

    const newEntries = Array.from(
      new Set(
        raw
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e && !existingLC.has(e.toLowerCase()))
          .map((e) => e.toLowerCase())
      )
    ).map(
      (lc) =>
        raw
          .split(",")
          .map((e) => e.trim())
          .find((e) => e.toLowerCase() === lc)!
    );

    if (newEntries.length) {
      setList([...(currentList ?? []), ...newEntries]);
    }
    setRaw("");
  };

  type MandatoryFieldKeys = keyof EditCareerSiteModel["fieldPreferences"];

  type MandatoryMap = {
    [K in MandatoryFieldKeys]: FieldPreference;
  };

  const mandatoryMap: MandatoryMap = useMemo(() => {
    const settings = careerSettings?.[0];
    if (!settings?.fieldPreferences) return {} as MandatoryMap;
    return settings.fieldPreferences;
  }, [careerSettings]);

  const isFieldShown = (key: keyof MandatoryMap) => mandatoryMap[key]?.isShown;
  const isFieldRequired = (key: keyof MandatoryMap) =>
    mandatoryMap[key]?.isRequired;

  //TODO: Optimize isFieldShown with a FOR loop when possible
  return (
    <div className="flex justify-center items-center bg-gray-100">
      <div
        className="fill-available max-w-xxl bg-white p-8 rounded-lg shadow-md"
        style={{
          borderRadius: radius,
        }}
      >
        <h2 className="text-2xl font-bold mb-6">{formTitle}</h2>
        {thankYouMessage ? (
          <div className="text-green-500">{thankYouMessage}</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {showOnlyResumeDrop ? (
              <>
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.uploadResume",
                      defaultMessage: "Upload Resume (PDF)",
                    })}
                    :
                  </label>
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileDrop}
                    className="text-center fill-available py-8 border-dashed border-2 border-slate-300 rounded-lg flex justify-center items-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {resumeFile ? (
                      <span className="text-green-500">{resumeFile.name}</span>
                    ) : (
                      <span>
                        {intl.formatMessage({
                          id: "careerPageApply.dragAndDropCv",
                          defaultMessage: "Drop your resume or click here",
                        })}
                      </span>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="application/pdf,application/vnd.oasis.opendocument.text,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={loading}
                    name="resumeFile"
                  />
                </div>
                <div>
                  <div className="flex justify-start mb-4 items-center">
                    <input
                      type="checkbox"
                      id="privacyConsent"
                      name="privacyConsent"
                      className="mr-2"
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                    />
                    <label
                      htmlFor="privacyConsent"
                      className="text-gray-700 text-left"
                    >
                      {intl.formatMessage({
                        id: "careerPageApply.privacyConsent",
                        defaultMessage: "I agree to the privacy policy",
                      })}
                    </label>
                  </div>
                  <div className="flex justify-start flex-wrap">
                    {privacyPolicyLinks.map((link, index) => (
                      <React.Fragment key={index}>
                        <a
                          href={
                            link.startsWith("http") ? link : `https://${link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline"
                        >
                          {`Policy ${index + 1}`}
                        </a>
                        {index < privacyPolicyLinks.length - 1 && (
                          <span> - </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.firstName",
                      defaultMessage: "First Name",
                    })}{" "}
                    :<span className="text-red-500">&nbsp;*</span>
                  </label>
                  <input
                    type="text"
                    value={name ?? ""}
                    onChange={(e) => setName(e.target.value)}
                    className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                    name="name"
                  />
                  {validationError && (!name || !name.trim()) && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.name",
                        defaultMessage: "First Name is required",
                      })}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.surname",
                      defaultMessage: "Surname",
                    })}{" "}
                    :<span className="text-red-500">&nbsp;*</span>
                  </label>
                  <input
                    type="text"
                    value={familyName ?? ""}
                    onChange={(e) => setFamilyName(e.target.value)}
                    className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                    name="familyName"
                  />
                  {validationError && !familyName?.trim() && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.surname",
                        defaultMessage: "Surname is required",
                      })}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.email",
                      defaultMessage: "Email",
                    })}{" "}
                    :<span className="text-red-500">&nbsp;*</span>
                  </label>
                  <input
                    type="email"
                    value={email ?? ""}
                    onChange={(e) => setEmail(e.target.value)}
                    className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                    name="email"
                  />
                  {validationError && !email?.trim() && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.email",
                        defaultMessage: "Email is required",
                      })}
                    </p>
                  )}
                  {validationError &&
                    email?.trim() &&
                    !/^\S+@\S+\.\S+$/.test(email) && (
                      <p className="text-red-500 text-sm mt-1">
                        {intl.formatMessage({
                          id: "careerPageApply.validation.emailFormat",
                          defaultMessage: "Invalid email format",
                        })}
                      </p>
                    )}
                </div>
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.phoneNumber",
                      defaultMessage: "Phone",
                    })}{" "}
                    :<span className="text-red-500">&nbsp;*</span>
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={phone ?? ""}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "");
                      setPhone(digitsOnly);
                    }}
                    onKeyDown={(e) => {
                      if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                    name="phone"
                  />
                  {validationError && !phone?.trim() && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.phone",
                        defaultMessage: "Phone is required",
                      })}
                    </p>
                  )}
                  {validationError &&
                    phone?.trim() &&
                    !/^\d{7,}$/.test(phone) && (
                      <p className="text-red-500 text-sm mt-1">
                        {intl.formatMessage({
                          id: "careerPageApply.validation.phoneFormat",
                          defaultMessage: "Invalid phone format",
                        })}
                      </p>
                    )}
                </div>
                {IsConvy && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.schoolType",
                        defaultMessage: "School Type:",
                      })}
                    </label>
                    <select
                      value={selectedSchoolType} // Use a single value state variable
                      onChange={handleSelectionSchoolType}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                      disabled={loading}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "careerPageApply.select",
                          defaultMessage: "Select",
                        })}
                      </option>
                      {convySchoolTypes.map((schoolType) => (
                        <option key={schoolType.id} value={schoolType.id}>
                          {schoolType.nome.it}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {IsConvy && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.studyPath",
                        defaultMessage: "Study Path:",
                      })}
                    </label>
                    <select
                      value={selectedStudyPath}
                      onChange={handleSelectionStudyPath}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                      disabled={loading}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "careerPageApply.select",
                          defaultMessage: "Select",
                        })}
                      </option>
                      {(convyStudyPaths || []).map((studyPath) => (
                        <option key={studyPath.id} value={studyPath.id}>
                          {studyPath.titolo.it}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {IsConvy && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.discipline",
                        defaultMessage: "Discipline:",
                      })}
                    </label>
                    <select
                      value={selectedDiscipline}
                      onChange={handleSelectionDiscipline}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                      disabled={loading}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "careerPageApply.select",
                          defaultMessage: "Select",
                        })}
                      </option>
                      {(convyDisciplines || []).map((disc) => (
                        <option key={disc.id} value={disc.id}>
                          {disc.titolo.it}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-left mb-2 text-gray-700">
                    {intl.formatMessage({
                      id: "careerPageApply.uploadResume",
                      defaultMessage: "Upload Resume (PDF)",
                    })}{" "}
                    :<span className="text-red-500">&nbsp;*</span>
                  </label>
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileDrop}
                    className="text-center fill-available py-8 border-dashed border-2 border-slate-300 rounded-lg flex justify-center items-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {resumeFile ? (
                      <span className="text-green-500">{resumeFile.name}</span>
                    ) : (
                      <span>
                        {intl.formatMessage({
                          id: "careerPageApply.dragAndDropCv",
                          defaultMessage: "Drop your resume or click here",
                        })}
                      </span>
                    )}
                    <input
                      type="file"
                      accept="application/pdf,application/vnd.oasis.opendocument.text,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={loading}
                    />
                  </div>
                  {validationError && !resumeFile && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.resume",
                        defaultMessage: "Resume is required",
                      })}
                    </p>
                  )}
                </div>
                {isFieldShown("address") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageWidget.searchAddress",
                        defaultMessage: "Search Address",
                      })}
                      :
                      {!isAddressAutoCompleteCompiled && (
                        <span className="text-red-500">&nbsp;*</span>
                      )}
                    </label>
                    <AddressAutocomplete
                      onSelectAddress={handleAddressSelection}
                      loading={loading}
                      setLoading={setLoading}
                    />

                    <div className="mt-4 space-y-2 mb-6">
                      <div>
                        <label className="block text-left mb-2 text-gray-700">
                          {intl.formatMessage({
                            id: "careerPageApply.address",
                            defaultMessage: "Street Address",
                          })}
                          :
                          {isFieldRequired("address") && (
                            <span className="text-red-500">&nbsp;*</span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={address || ""}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "careerPageApply.addressHint",
                          })}
                          className="text-left w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={loading || !address}
                          required={isFieldRequired("address")}
                          name="address"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-left mb-2 text-gray-700">
                            {intl.formatMessage({
                              id: "careerPageApply.city",
                              defaultMessage: "City",
                            })}
                            :
                            {isFieldRequired("address") && (
                              <span className="text-red-500">&nbsp;*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={city || ""}
                            onChange={(e) => setCity(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: "careerPageApply.cityHint",
                            })}
                            className="text-left w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || !address}
                            required={isFieldRequired("address")}
                            name="city"
                          />
                        </div>
                        <div>
                          <label className="block text-left mb-2 text-gray-700">
                            {intl.formatMessage({
                              id: "careerPageApply.province",
                              defaultMessage: "Province",
                            })}
                            :
                            {isFieldRequired("address") && (
                              <span className="text-red-500">&nbsp;*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={province || ""}
                            onChange={(e) => setProvince(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: "careerPageApply.provinceHint",
                            })}
                            className="text-left w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || !address}
                            required={isFieldRequired("address")}
                            name="province"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label className="block text-left mb-2 text-gray-700">
                            {intl.formatMessage({
                              id: "careerPageApply.postalCode",
                              defaultMessage: "Postal Code",
                            })}
                            :
                            {isFieldRequired("address") && (
                              <span className="text-red-500">&nbsp;*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={postalCode || ""}
                            onChange={(e) => setPostalCode(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: "careerPageApply.postalCodeHint",
                            })}
                            className="text-left w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || !address}
                            required={isFieldRequired("address")}
                            name="postalCode"
                          />
                        </div>
                        <div>
                          <label className="block text-left mb-2 text-gray-700">
                            {intl.formatMessage({
                              id: "careerPageApply.state",
                              defaultMessage: "Country",
                            })}
                            :
                            {isFieldRequired("address") && (
                              <span className="text-red-500">&nbsp;*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={country || ""}
                            onChange={(e) => setCountry(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: "careerPageApply.stateHint",
                            })}
                            className="text-left w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || !address}
                            required={isFieldRequired("address")}
                            name="country"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {isFieldShown("dateOfBirth") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.birthDate",
                        defaultMessage: "Birth Date",
                      })}{" "}
                      :
                      {isFieldRequired("dateOfBirth") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="date"
                      lang={intl.locale}
                      value={birthDate ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          const year = value.split("-")[0];
                          if (year.length <= 4) {
                            setBirthDate(value);
                          } else {
                            const truncatedValue = value.replace(
                              year,
                              year.substring(0, 4)
                            );
                            setBirthDate(truncatedValue);
                          }
                        } else {
                          setBirthDate("");
                        }
                      }}
                      required={isFieldRequired("dateOfBirth")}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                      name="birthDate"
                    />
                  </div>
                )}
                {isFieldShown("gender") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.gender",
                        defaultMessage: "Gender",
                      })}{" "}
                      :
                      {isFieldRequired("gender") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <select
                      value={gender ?? ""}
                      onChange={(e) => setGender(e.target.value)}
                      required={isFieldRequired("gender")}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: "careerPageApply.select",
                          defaultMessage: "Select",
                        })}
                      </option>
                      <option value="Gender.male">
                        {intl.formatMessage({
                          id: "careerPageApply.male",
                          defaultMessage: "Male",
                        })}
                      </option>
                      <option value="Gender.female">
                        {intl.formatMessage({
                          id: "careerPageApply.female",
                          defaultMessage: "Female",
                        })}
                      </option>
                      <option value="Gender.nonBinary">
                        {intl.formatMessage({
                          id: "careerPageApply.nonBinary",
                          defaultMessage: "Non-Binary",
                        })}
                      </option>
                      <option value="Gender.preferNotToSay">
                        {intl.formatMessage({
                          id: "careerPageApply.preferNotToSay",
                          defaultMessage: "Prefer not to say",
                        })}
                      </option>
                    </select>
                  </div>
                )}
                {isFieldShown("nationality") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.nationality",
                        defaultMessage: "Nationality",
                      })}
                      :
                      {isFieldRequired("nationality") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <select
                      value={nationality ?? ""}
                      onChange={(e) => setNationality(e.target.value)}
                      required={isFieldRequired("nationality")}
                      className="text-left fill-available p-2 border rounded-lg bg-white"
                      disabled={loading}
                    >
                      <option value="" disabled>
                        {intl.formatMessage({
                          id: "careerPageApply.selectNationality",
                          defaultMessage: "Select your nationality",
                        })}
                      </option>
                      {nationalities.map((nat) => (
                        <option key={nat} value={nat}>
                          {nat}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {(isFieldShown("protectedCategoryStatus") ||
                  isFieldShown("disabilityPercentage")) && (
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    {isFieldShown("protectedCategoryStatus") && (
                      <div className="flex-none flex flex-col text-left">
                        <label className="block mb-2 text-gray-700">
                          {intl.formatMessage({
                            id: "careerPageApply.protectedCategoryStatus",
                            defaultMessage: "Protected Category Status",
                          })}
                          :
                          {isFieldRequired("protectedCategoryStatus") && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="protectedCategoryStatus"
                              className="mr-2"
                              onChange={() => {
                                setProtectedCategoryStatus(true);
                                setDisabilityPercentage((prev) =>
                                  (prev ?? 0) < 1 ? 1 : prev!
                                );
                              }}
                              checked={protectedCategoryStatus === true}
                              disabled={loading}
                              required={isFieldRequired(
                                "protectedCategoryStatus"
                              )}
                            />
                            {intl.formatMessage({
                              id: "global.yes",
                              defaultMessage: "Yes",
                            })}
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="protectedCategoryStatus"
                              className="mr-2"
                              onChange={() => {
                                setProtectedCategoryStatus(false);
                                setDisabilityPercentage(0);
                              }}
                              checked={protectedCategoryStatus === false}
                              disabled={loading}
                              required={isFieldRequired(
                                "protectedCategoryStatus"
                              )}
                            />
                            {intl.formatMessage({
                              id: "global.no",
                              defaultMessage: "No",
                            })}
                          </label>
                        </div>
                      </div>
                    )}

                    {isFieldShown("disabilityPercentage") && (
                      <div className="flex-1 flex flex-col text-left">
                        <label className="block mb-2 text-gray-700">
                          {intl.formatMessage({
                            id: "careerPageApply.disabilityPercentage",
                            defaultMessage: "Disability Percentage",
                          })}
                          :
                          {isFieldRequired("disabilityPercentage") && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <input
                          type="number"
                          value={disabilityPercentage ?? ""}
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const raw = parseInt(e.target.value, 10);
                            let clamped: number | null;
                            if (isNaN(raw)) {
                              clamped = null;
                            } else if (raw < 0) {
                              clamped = 0;
                            } else if (raw > 100) {
                              clamped = 100;
                            } else {
                              clamped = raw;
                            }
                            setDisabilityPercentage(clamped);
                            setProtectedCategoryStatus((clamped ?? 0) > 0);
                          }}
                          required={isFieldRequired("disabilityPercentage")}
                          disabled={loading}
                          min={0}
                          max={100}
                          step={1}
                          className="w-90 p-2 border rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                )}
                {isFieldShown("drivingLicense") && (
                  <div className="radio-group flex flex-col text-left">
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.drivingLicense",
                        defaultMessage: "Driving License",
                      })}{" "}
                      :
                      {isFieldRequired("drivingLicense") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-row items-center gap-4">
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="drivingLicense"
                          onChange={() => setDrivingLicense(true)}
                          checked={drivingLicense === true}
                          disabled={loading}
                          required={isFieldRequired("drivingLicense")}
                        />
                        {intl.formatMessage({
                          id: "global.yes",
                          defaultMessage: "Yes",
                        })}
                      </label>
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="drivingLicense"
                          onChange={() => setDrivingLicense(false)}
                          checked={drivingLicense === false}
                          disabled={loading}
                          required={isFieldRequired("drivingLicense")}
                        />
                        {intl.formatMessage({
                          id: "global.no",
                          defaultMessage: "No",
                        })}
                      </label>
                    </div>
                  </div>
                )}
                {isFieldShown("felonyConviction") && (
                  <div className="radio-group flex flex-col text-left">
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.felonyConviction",
                        defaultMessage: "Felony Conviction",
                      })}{" "}
                      :
                      {isFieldRequired("felonyConviction") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-row items-center gap-4">
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="felonyConviction"
                          onChange={() => setFelonyConviction(true)}
                          checked={felonyConviction === true}
                          disabled={loading}
                          required={isFieldRequired("felonyConviction")}
                        />
                        {intl.formatMessage({
                          id: "global.yes",
                          defaultMessage: "Yes",
                        })}
                      </label>
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="felonyConviction"
                          onChange={() => setFelonyConviction(false)}
                          checked={felonyConviction === false}
                          disabled={loading}
                          required={isFieldRequired("felonyConviction")}
                        />
                        {intl.formatMessage({
                          id: "global.no",
                          defaultMessage: "No",
                        })}
                      </label>
                    </div>
                  </div>
                )}
                {isFieldShown("citizenWorkStatus") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.citizenWorkStatus",
                        defaultMessage: "Citizen Work Status",
                      })}{" "}
                      :
                      {isFieldRequired("citizenWorkStatus") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={citizenWorkStatus ?? ""}
                      onChange={(e) => setCitizenWorkStatus(e.target.value)}
                      required={isFieldRequired("citizenWorkStatus")}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                  </div>
                )}
                {isFieldShown("applicantPreferences") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.applicantPreferences",
                        defaultMessage: "Applicant Preferences",
                      })}{" "}
                      :
                      {isFieldRequired("applicantPreferences") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={applicantPreferences ?? ""}
                      onChange={(e) => setApplicantPreferences(e.target.value)}
                      required={isFieldRequired("applicantPreferences")}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                  </div>
                )}
                {isFieldShown("fiscalCode") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.fiscalCode",
                        defaultMessage: "Fiscal Code",
                      })}{" "}
                      :
                      {isFieldRequired("fiscalCode") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={fiscalCode ?? ""}
                      onChange={(e) => setFiscalCode(e.target.value)}
                      required={isFieldRequired("fiscalCode")}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                      name="pilotFlightHours"
                    />
                  </div>
                )}
                {isFieldShown("commercialLicense") && (
                  <div className="radio-group flex flex-col text-left">
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.commercialLicense",
                        defaultMessage: "Commercial License",
                      })}{" "}
                      :
                      {isFieldRequired("commercialLicense") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-row items-center gap-4">
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="commercialLicense"
                          onChange={() => setCommercialLicense(true)}
                          checked={commercialLicense === true}
                          disabled={loading}
                          required={isFieldRequired("commercialLicense")}
                        />
                        {intl.formatMessage({
                          id: "global.yes",
                          defaultMessage: "Yes",
                        })}
                      </label>
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="commercialLicense"
                          onChange={() => setCommercialLicense(false)}
                          checked={commercialLicense === false}
                          disabled={loading}
                          required={isFieldRequired("commercialLicense")}
                        />
                        {intl.formatMessage({
                          id: "global.no",
                          defaultMessage: "No",
                        })}
                      </label>
                    </div>
                  </div>
                )}
                {isFieldShown("pilotFlightHours") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.pilotFlightHours",
                        defaultMessage: "Pilot Flight Hours",
                      })}{" "}
                      :
                      {isFieldRequired("pilotFlightHours") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={pilotFlightHours ?? ""}
                      onChange={(e) =>
                        setPilotFlightHours(Number(e.target.value))
                      }
                      required={isFieldRequired("pilotFlightHours")}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                    />
                  </div>
                )}
                {isFieldShown("jobQualification") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.jobQualification",
                        defaultMessage: "Job Qualification",
                      })}{" "}
                      :
                      {isFieldRequired("jobQualification") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={jobQualification ?? ""}
                      onChange={(e) => setJobQualification(e.target.value)}
                      required={isFieldRequired("jobQualification")}
                      className="text-left fill-available p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    />
                  </div>
                )}
                {isFieldShown("softSkills") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.softSkills",
                        defaultMessage: "Soft Skills",
                      })}
                      :
                      {isFieldRequired("softSkills") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {softSkills?.map((skill, i) => (
                        <span
                          key={i}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() =>
                              setSoftSkills(
                                softSkills.filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-blue-500 hover:text-blue-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={softSkillInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          handleCommaSeparatedInput(
                            v.slice(0, -2),
                            softSkills,
                            setSoftSkills,
                            setSoftSkillInput
                          );
                        } else {
                          setSoftSkillInput(v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCommaSeparatedInput(
                            softSkillInput,
                            softSkills,
                            setSoftSkills,
                            setSoftSkillInput
                          );
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.softSkills",
                            defaultMessage: "Soft Skills",
                          }),
                        }
                      )}
                      name="softSkillInput"
                    />
                  </div>
                )}
                {isFieldShown("hardSkills") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.hardSkills",
                        defaultMessage: "Hard Skills",
                      })}
                      :
                      {isFieldRequired("hardSkills") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {hardSkills?.map((skill, i) => (
                        <span
                          key={i}
                          className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() =>
                              setHardSkills(
                                hardSkills.filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-green-500 hover:text-green-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={hardSkillInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          handleCommaSeparatedInput(
                            v.slice(0, -2),
                            hardSkills,
                            setHardSkills,
                            setHardSkillInput
                          );
                        } else {
                          setHardSkillInput(v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCommaSeparatedInput(
                            hardSkillInput,
                            hardSkills,
                            setHardSkills,
                            setHardSkillInput
                          );
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.hardSkills",
                            defaultMessage: "Hard Skills",
                          }),
                        }
                      )}
                      name="hardSkillInput"
                    />
                  </div>
                )}
                {isFieldShown("certifications") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.certifications",
                        defaultMessage: "Certifications",
                      })}
                      :
                      {isFieldRequired("certifications") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {certifications?.map((cert, i) => (
                        <span
                          key={i}
                          className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {cert}
                          <button
                            type="button"
                            onClick={() =>
                              setCertifications(
                                certifications.filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-blue-500 hover:text-blue-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={certificationsInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          handleCommaSeparatedInput(
                            v.slice(0, -2),
                            certifications,
                            setCertifications,
                            setCertificationsInput
                          );
                        } else {
                          setCertificationsInput(v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCommaSeparatedInput(
                            certificationsInput,
                            certifications,
                            setCertifications,
                            setCertificationsInput
                          );
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.certifications",
                            defaultMessage: "Certifications",
                          }),
                        }
                      )}
                      name="certificationsInput"
                    />
                  </div>
                )}
                {isFieldShown("hobbyAndInterests") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.hobbyAndInterests",
                        defaultMessage: "Hobby and Interests",
                      })}
                      :
                      {isFieldRequired("hobbyAndInterests") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {hobbyAndInterests?.map((h, i) => (
                        <span
                          key={i}
                          className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {h}
                          <button
                            type="button"
                            onClick={() =>
                              setHobbyAndInterests(
                                hobbyAndInterests.filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-yellow-500 hover:text-yellow-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={hobbyAndInterestsInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          handleCommaSeparatedInput(
                            v.slice(0, -2),
                            hobbyAndInterests,
                            setHobbyAndInterests,
                            setHobbyAndInterestsInput
                          );
                        } else {
                          setHobbyAndInterestsInput(v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCommaSeparatedInput(
                            hobbyAndInterestsInput,
                            hobbyAndInterests,
                            setHobbyAndInterests,
                            setHobbyAndInterestsInput
                          );
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.hobbyAndInterests",
                            defaultMessage: "Hobby and Interests",
                          }),
                        }
                      )}
                      name="hobbyAndInterestsInput"
                    />
                  </div>
                )}
                {isFieldShown("spokenLanguages") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.spokenLanguages",
                        defaultMessage: "Spoken Languages",
                      })}
                      :
                      {isFieldRequired("spokenLanguages") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {spokenLanguages?.map((lang, i) => (
                        <span
                          key={i}
                          className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {lang}
                          <button
                            type="button"
                            onClick={() =>
                              setSpokenLanguages(
                                spokenLanguages.filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-purple-500 hover:text-purple-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={spokenLanguagesInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          handleCommaSeparatedInput(
                            v.slice(0, -2),
                            spokenLanguages,
                            setSpokenLanguages,
                            setSpokenLanguagesInput
                          );
                        } else {
                          setSpokenLanguagesInput(v);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCommaSeparatedInput(
                            spokenLanguagesInput,
                            spokenLanguages,
                            setSpokenLanguages,
                            setSpokenLanguagesInput
                          );
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.spokenLanguages",
                            defaultMessage: "Spoken Languages",
                          }),
                        }
                      )}
                      name="spokenLanguagesInput"
                    />
                  </div>
                )}
                {isFieldShown("links") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.links",
                        defaultMessage: "Links",
                      })}
                      :
                      {isFieldRequired("links") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(links ?? []).map((link, i) => (
                        <span
                          key={i}
                          className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {link}
                          <button
                            type="button"
                            onClick={() =>
                              setLinks(
                                (links ?? []).filter((_, idx) => idx !== i)
                              )
                            }
                            className="ml-2 text-purple-500 hover:text-purple-700"
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={linksInput ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.endsWith(", ")) {
                          const trimmed = v.slice(0, -2).trim();
                          const validLink = /\.\S+/.test(trimmed);
                          if (!validLink) {
                            setLinkError(
                              intl.formatMessage({
                                id: "careerPageApply.validation.linkFormat",
                                defaultMessage:
                                  'The link must contain at least one "." followed by characters',
                              })
                            );
                          } else if (!(links ?? []).includes(trimmed)) {
                            setLinks([...(links ?? []), trimmed]);
                            setLinksInput("");
                            setLinkError(null);
                          }
                        } else {
                          setLinksInput(v);
                          if (linkError) setLinkError(null);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = linksInput?.trim() || "";
                          const validLink = /\.\S+/.test(trimmed);
                          if (!validLink) {
                            setLinkError(
                              intl.formatMessage({
                                id: "careerPageApply.validation.linkFormat",
                                defaultMessage:
                                  'The link must contain at least one "." followed by characters',
                              })
                            );
                          } else if (!(links ?? []).includes(trimmed)) {
                            setLinks([...(links ?? []), trimmed]);
                            setLinksInput("");
                            setLinkError(null);
                          }
                        }
                      }}
                      className="text-left fill-available p-2 border rounded-lg"
                      disabled={loading}
                      placeholder={intl.formatMessage(
                        {
                          id: "careerPageApply.typeAndEnter",
                          defaultMessage: "Type and press Enter",
                        },
                        {
                          input: intl.formatMessage({
                            id: "careerPageApply.links",
                            defaultMessage: "Links",
                          }),
                        }
                      )}
                      name="linksInput"
                    />
                    {linkError && (
                      <p className="text-red-500 text-sm mt-1">{linkError}</p>
                    )}
                  </div>
                )}
                {isFieldShown("willingnessToTravel") && (
                  <div className="radio-group flex flex-col text-left">
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.willingnessToTravel",
                        defaultMessage: "Willingness to Travel",
                      })}{" "}
                      :
                      {isFieldRequired("willingnessToTravel") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <div className="flex flex-row items-center gap-4">
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="willingnessToTravel"
                          onChange={() => setWillingnessToTravel(true)}
                          checked={willingnessToTravel === true}
                          disabled={loading}
                          required={isFieldRequired("willingnessToTravel")}
                        />
                        {intl.formatMessage({
                          id: "global.yes",
                          defaultMessage: "Yes",
                        })}
                      </label>
                      <label className="flex flex-row items-center">
                        <input
                          className="mr-2"
                          type="radio"
                          name="willingnessToTravel"
                          onChange={() => setWillingnessToTravel(false)}
                          checked={willingnessToTravel === false}
                          disabled={loading}
                          required={isFieldRequired("willingnessToTravel")}
                        />
                        {intl.formatMessage({
                          id: "global.no",
                          defaultMessage: "No",
                        })}
                      </label>
                    </div>
                  </div>
                )}
                {isFieldShown("workAvailability") && (
                  <div>
                    <h3 className="text-left mb-2 text-gray-700 font-semibold">
                      {intl.formatMessage({
                        id: "careerPageApply.workAvailability",
                        defaultMessage: "Work Availability",
                      })}{" "}
                      :
                      {isFieldRequired("workAvailability") && (
                        <span className="text-red-500">*</span>
                      )}
                    </h3>
                    <div className="mb-4">
                      <label className="block text-left mb-2 text-gray-700">
                        {intl.formatMessage({
                          id: "careerPageApply.startDate",
                          defaultMessage: "Start Date:",
                        })}
                      </label>
                      <input
                        type="date"
                        value={
                          workAvailability?.availabilityStartDate
                            ? workAvailability.availabilityStartDate
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            const year = value.split("-")[0];
                            if (year.length <= 4) {
                              setWorkAvailability((prev) => ({
                                ...((prev as WorkAvailability) || {
                                  availabilityStartDate: "",
                                  nightAvailability: false,
                                  weekendAvailability: false,
                                  willRelocate: false,
                                  workOvertime: false,
                                }),
                                availabilityStartDate: value,
                              }));
                            } else {
                              const truncatedValue = value.replace(
                                year,
                                year.substring(0, 4)
                              );
                              setWorkAvailability((prev) => ({
                                ...((prev as WorkAvailability) || {
                                  availabilityStartDate: "",
                                  nightAvailability: false,
                                  weekendAvailability: false,
                                  willRelocate: false,
                                  workOvertime: false,
                                }),
                                availabilityStartDate: truncatedValue,
                              }));
                            }
                          } else {
                            setWorkAvailability((prev) => ({
                              ...((prev as WorkAvailability) || {
                                availabilityStartDate: "",
                                nightAvailability: false,
                                weekendAvailability: false,
                                willRelocate: false,
                                workOvertime: false,
                              }),
                              availabilityStartDate: "",
                            }));
                          }
                        }}
                        className="text-left fill-available p-2 border rounded-lg"
                        disabled={loading}
                        required={isFieldRequired("workAvailability")}
                        name="availabilityStartDate"
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      {[
                        "nightAvailability",
                        "weekendAvailability",
                        "willRelocate",
                        "workOvertime",
                      ].map((field) => (
                        <div className="text-left" key={field}>
                          <label className="block mb-2 text-gray-700">
                            {intl.formatMessage({
                              id: `careerPageApply.${field}`,
                              defaultMessage: field
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase()),
                            })}
                          </label>
                          <div className="flex flex-row items-center gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={field}
                                className="mr-2"
                                onChange={() =>
                                  setWorkAvailability((prev) => ({
                                    ...(prev || {
                                      availabilityStartDate: "",
                                      nightAvailability: false,
                                      weekendAvailability: false,
                                      willRelocate: false,
                                      workOvertime: false,
                                    }),
                                    [field as keyof WorkAvailability]: true,
                                  }))
                                }
                                checked={
                                  workAvailability?.[
                                    field as keyof WorkAvailability
                                  ] === true
                                }
                                disabled={loading}
                              />

                              {intl.formatMessage({
                                id: "global.yes",
                                defaultMessage: "Yes",
                              })}
                            </label>

                            <label className="flex items-center">
                              <input
                                className="mr-2"
                                type="radio"
                                name={field}
                                onChange={() =>
                                  setWorkAvailability((prev) => ({
                                    ...((prev as WorkAvailability) || {
                                      availabilityStartDate: "",
                                      nightAvailability: false,
                                      weekendAvailability: false,
                                      willRelocate: false,
                                      workOvertime: false,
                                    }),
                                    [field as keyof WorkAvailability]: false,
                                  }))
                                }
                                checked={
                                  workAvailability?.[
                                    field as keyof WorkAvailability
                                  ] === false
                                }
                                disabled={loading}
                              />

                              {intl.formatMessage({
                                id: "global.no",
                                defaultMessage: "No",
                              })}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Dynamic Form Section */}
                {forms.map((form) => (
                  <React.Fragment key={form.formID}>
                    {form.questions?.map((question) => (
                      <div key={question.questionID} className="text-left">
                        <label className="block text-left mb-2 text-gray-700">
                          {question.questionText}
                          {question.isMandatory && (
                            <span className="text-red-500">&nbsp;*</span>
                          )}
                        </label>
                        <DynamicField
                          question={question}
                          onChange={(value: any) =>
                            handleDynamicFieldChange(question.questionID, value)
                          }
                          disabled={loading}
                          careerSettings={careerSettings[0]}
                        />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
                {isFieldShown("desiredSalary") && (
                  <div>
                    <label className="block text-left mb-2 text-gray-700">
                      {intl.formatMessage({
                        id: "careerPageApply.desiredSalary",
                        defaultMessage: "Desired gross annual salary",
                      })}
                      :
                      {isFieldRequired("desiredSalary") && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={desiredSalaryInput}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/[\s.,]/g, "");
                        let num = digits === "" ? 0 : parseInt(digits, 10);
                        if (num < 0) num = 0;
                        if (num > 10_000_000) num = 10_000_000;
                        setDesiredSalary(num);
                        setDesiredSalaryInput(
                          formatSalary(num.toString(), intl.locale)
                        );
                      }}
                      onBlur={() => {
                        if (desiredSalary === null) {
                          setDesiredSalaryInput("");
                        }
                      }}
                      required={isFieldRequired("desiredSalary")}
                      disabled={loading}
                      className="w-full text-left p-2 border rounded-lg"
                    />
                  </div>
                )}
                <div>
                  {IsConvy && (
                    <div className="flex justify-start items-center mb-4">
                      <input
                        type="checkbox"
                        id="newsletterConsent"
                        className="mr-2"
                        checked={newsletterConsent}
                        onChange={(e) => {
                          setnewsletterConsent(e.target.checked);
                          if (e.target.checked) {
                            setValidationError(null);
                          }
                        }}
                      />
                      <div className="flex flex-col lg:flex-column justify-between items-left ">
                        <label
                          htmlFor="newsletterConsent"
                          className="text-green-400 md:font-bold text-left"
                        >
                          {intl.formatMessage({
                            id: "careerPageApply.newsletterConsent",
                            defaultMessage: "I agree to Convy+'s newsletter",
                          })}
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-start items-center mb-4">
                    <input
                      type="checkbox"
                      id="privacyConsent"
                      className="mr-2"
                      checked={privacyConsent}
                      required
                      onChange={(e) => {
                        setPrivacyConsent(e.target.checked);
                        if (e.target.checked) {
                          setValidationError(null);
                        }
                      }}
                      name="privacyConsent"
                    />
                    <label
                      htmlFor="privacyConsent"
                      className="text-gray-700 text-left"
                    >
                      {intl.formatMessage({
                        id: "careerPageApply.privacyConsent",
                        defaultMessage: "I agree to the privacy policy",
                      })}
                      <span className="text-red-500">&nbsp;*</span>
                    </label>
                  </div>

                  <div className="flex justify-start flex-wrap">
                    {privacyPolicyLinks.map((link, index) => (
                      <React.Fragment key={index}>
                        <a
                          href={
                            link.startsWith("http") ? link : `https://${link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline"
                        >
                          {`Policy ${index + 1}`}
                        </a>
                        {index < privacyPolicyLinks.length - 1 && (
                          <span> - </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {validationError && !privacyConsent && (
                    <p className="text-red-500 text-sm mt-1">
                      {intl.formatMessage({
                        id: "careerPageApply.validation.privacyConsent",
                        defaultMessage: "Please agree to the privacy policy",
                      })}
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="text-center mt-6">
              <button
                type="submit"
                className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 fill-available rounded-full transition duration-150 ease-in-out no-underline text-sm"
                style={{
                  backgroundColor: `#${color}`,
                }}
                disabled={loading}
              >
                {loading
                  ? intl.formatMessage({
                      id: "careerPageApply.submitting",
                      defaultMessage: "Submitting...",
                    })
                  : buttonText}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResumeForm;
